import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  Badge,
  Banner,
  Button,
  Card,
  ChipGroup,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "@magarac/ui";
import { jobSchema } from "@magarac/shared/schemas/job";
import { jobTypeListSchema } from "@magarac/shared/schemas/jobType";
import {
  REQUEST_STATUSES,
  requestPageSchema,
  requestResponseSchema,
  workRequestSchema,
  type RequestStatus,
  type WorkRequest,
} from "@magarac/shared/schemas/request";
import { HelpIcon } from "../../../components/HelpIcon";
import { NotesPanel } from "../../../components/NotesPanel";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { formatRelative, REQUEST_STATUS_LABEL } from "../../../lib/format";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/requests/")({
  component: RequestsPage,
});

type Filter = "all" | RequestStatus;

const SOURCE_LABEL: Record<WorkRequest["source"], string> = {
  call: "Phone call",
  booking_page: "Booking page",
  website_form: "Website",
  manual: "Added by the office",
};

/** CRM-7..CRM-9: requests list with status filter; no board. Screen key: requests.list */
function RequestsPage() {
  const { active } = useTenant();
  const [filter, setFilter] = useState<Filter>("new");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<WorkRequest | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["requests", active?.id, "list", filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get("/requests", requestPageSchema, {
        query: { limit: 50, cursor: pageParam, status: filter === "all" ? undefined : filter },
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(active),
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Requests"
        subtitle="Calls, website forms, and anything else that is not a job yet."
        help={<HelpIcon screen="requests.list" />}
        actions={<Button onClick={() => setAdding(true)}>Add request</Button>}
      />
      <div className="mb-4">
        <ChipGroup
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all" as Filter, label: "All" },
            ...REQUEST_STATUSES.map((s) => ({
              value: s as Filter,
              label: REQUEST_STATUS_LABEL[s].label,
            })),
          ]}
        />
      </div>
      {q.isLoading ? <Spinner /> : null}
      {q.data && items.length === 0 ? (
        <EmptyState title={filter === "new" ? "No new requests" : "Nothing here"} />
      ) : null}
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => setOpen(r)}
              className="block w-full rounded-card border border-line bg-surface p-4 text-left hover:bg-brand-50"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{r.customerName ?? "Unknown caller"}</span>
                <span className="flex gap-1">
                  {r.urgency === "urgent" ? <Badge tone="danger">Urgent</Badge> : null}
                  <Badge tone={REQUEST_STATUS_LABEL[r.status].tone}>
                    {REQUEST_STATUS_LABEL[r.status].label}
                  </Badge>
                </span>
              </div>
              <p className="mt-1 line-clamp-2 text-base">{r.description}</p>
              <p className="mt-1 text-xs text-muted">
                {SOURCE_LABEL[r.source]} · {formatRelative(r.createdAt)}
              </p>
            </button>
          </li>
        ))}
      </ul>
      {q.hasNextPage ? (
        <Button
          variant="ghost"
          className="mt-3"
          onClick={() => void q.fetchNextPage()}
          busy={q.isFetchingNextPage}
        >
          Show more
        </Button>
      ) : null}
      <NewRequestDialog open={adding} onClose={() => setAdding(false)} />
      {open ? <RequestDialog request={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function NewRequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [preferred, setPreferred] = useState("");
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setFirstName("");
    setLastName("");
    setPhone("");
    setDescription("");
    setUrgent(false);
    setPreferred("");
    setError(null);
    onClose();
  };
  const create = useMutation({
    mutationFn: () =>
      api.post(
        "/requests",
        {
          source: "manual",
          description,
          urgency: urgent ? "urgent" : "normal",
          ...(preferred ? { preferredTimes: preferred } : {}),
          ...(firstName
            ? {
                newCustomer: {
                  firstName,
                  ...(lastName ? { lastName } : {}),
                  ...(phone ? { phone } : {}),
                },
              }
            : {}),
        },
        requestResponseSchema,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["requests"] });
      close();
    },
    onError: (err) => setError(messageFor(err)),
  });
  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a request"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            busy={create.isPending}
            disabled={!description.trim()}
          >
            Save request
          </Button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <p className="mb-3 text-sm text-muted">
        For someone who is already a customer, add the request from their page instead.
      </p>
      <div className="grid gap-x-3 md:grid-cols-2">
        <Field id="nr-first" label="First name">
          <Input id="nr-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field id="nr-last" label="Last name">
          <Input id="nr-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>
      <Field id="nr-phone" label="Phone">
        <Input id="nr-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field id="nr-desc" label="What they need">
        <Textarea
          id="nr-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field id="nr-pref" label="Good times for them (optional)">
        <Input
          id="nr-pref"
          value={preferred}
          onChange={(e) => setPreferred(e.target.value)}
          placeholder="Weekday mornings"
        />
      </Field>
      <label className="flex min-h-11 items-center gap-2">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={urgent}
          onChange={(e) => setUrgent(e.target.checked)}
        />
        Urgent
      </label>
    </Dialog>
  );
}

function RequestDialog({ request, onClose }: { request: WorkRequest; onClose: () => void }) {
  const { active } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [jobTypeId, setJobTypeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const jobTypes = useQuery({
    queryKey: ["jobTypes", active?.id],
    queryFn: () => api.get("/job-types", jobTypeListSchema),
    enabled: Boolean(active),
  });
  const setStatus = useMutation({
    mutationFn: (status: RequestStatus) =>
      api.patch(`/requests/${request.id}`, { status }, requestResponseSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["requests"] });
      onClose();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const convert = useMutation({
    mutationFn: () =>
      api.post(
        `/requests/${request.id}/convert`,
        { jobTypeId },
        z.object({ request: workRequestSchema, job: jobSchema }),
      ),
    onSuccess: async ({ job }) => {
      await queryClient.invalidateQueries({ queryKey: ["requests"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
      await navigate({ to: "/jobs/$id", params: { id: job.id } });
    },
    onError: (err) => setError(messageFor(err)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={request.customerName ?? "Request"}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <p className="whitespace-pre-wrap text-base">{request.description}</p>
      {request.preferredTimes ? (
        <p className="mt-2 text-sm text-muted">Good times: {request.preferredTimes}</p>
      ) : null}
      {request.customerId ? (
        <Link
          to="/customers/$id"
          params={{ id: request.customerId }}
          className="mt-2 inline-block text-brand-600 underline"
        >
          Open customer
        </Link>
      ) : null}
      {request.jobId ? (
        <Link
          to="/jobs/$id"
          params={{ id: request.jobId }}
          className="mt-2 block text-brand-600 underline"
        >
          Open the job made from this request
        </Link>
      ) : (
        <Card className="mt-4" title="Make it a job">
          <Field id="rq-type" label="Service">
            <Select id="rq-type" value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
              <option value="">Choose a service</option>
              {jobTypes.data?.jobTypes
                .filter((t) => t.active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Button
            onClick={() => convert.mutate()}
            busy={convert.isPending}
            disabled={!jobTypeId || !request.customerId}
          >
            Make it a job
          </Button>
          <Button
            variant="secondary"
            className="ml-2"
            disabled={!request.customerId}
            onClick={() => void navigate({ to: "/quotes/new", search: { requestId: request.id } })}
          >
            Quote it first
          </Button>
          {!request.customerId ? (
            <p className="mt-2 text-sm text-muted">Add the customer first.</p>
          ) : (
            <p className="mt-2 text-sm text-muted">
              Quote it first to get the customer's approval (and a deposit) before making the job.
            </p>
          )}
        </Card>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {(["contacted", "quoted", "lost"] as const)
          .filter((s) => s !== request.status)
          .map((s) => (
            <Button
              key={s}
              variant="secondary"
              onClick={() => setStatus.mutate(s)}
              busy={setStatus.isPending && setStatus.variables === s}
            >
              Mark {REQUEST_STATUS_LABEL[s].label.toLowerCase()}
            </Button>
          ))}
      </div>
      <div className="mt-4">
        <NotesPanel parentType="request" parentId={request.id} />
      </div>
    </Dialog>
  );
}
